import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import { fmtDate, selectCls } from "./shared";

const EMPTY = { code: "", type: "percent", value: "", expires_at: "", max_uses: "" };
const inputCls = "h-10 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm";

const codeState = (c) => {
  if (!c.active) return ["Disattivo", "bg-[#e4e7e9] text-[#6b7280]"];
  if (c.expires_at && new Date(c.expires_at) < new Date()) return ["Scaduto", "bg-[#ef4444] text-white"];
  if (c.max_uses && c.uses >= c.max_uses) return ["Esaurito", "bg-[#f59e0b] text-black"];
  return ["Attivo", "bg-[#10b981] text-white"];
};

const CreateForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.createDiscountCode({ code: f.code, type: f.type, value: Number(f.value),
        expires_at: f.expires_at || null, max_uses: f.max_uses ? Number(f.max_uses) : null });
      toast.success(`Codice ${f.code.toUpperCase()} creato`);
      setF(EMPTY);
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };
  return (
    <form data-testid="discount-create-form" onSubmit={submit} className="border border-[#d5dbde] p-5 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
      <label className="col-span-2 md:col-span-1 text-[10px] uppercase font-mono text-[#6b7280] space-y-1">Codice
        <Input data-testid="dc-code" required value={f.code} onChange={set("code")} placeholder="BENVENUTO10" className={`${inputCls} uppercase font-mono`} />
      </label>
      <label className="text-[10px] uppercase font-mono text-[#6b7280] space-y-1 flex flex-col">Tipo
        <select data-testid="dc-type" value={f.type} onChange={set("type")} className={selectCls}>
          <option value="percent">Percentuale %</option><option value="fixed">Importo fisso</option>
        </select>
      </label>
      <label className="text-[10px] uppercase font-mono text-[#6b7280] space-y-1">{f.type === "percent" ? "Sconto %" : "Sconto (EUR/CHF)"}
        <Input data-testid="dc-value" required type="number" min="1" max={f.type === "percent" ? 100 : 10000} step="0.01" value={f.value} onChange={set("value")} className={inputCls} />
      </label>
      <label className="text-[10px] uppercase font-mono text-[#6b7280] space-y-1">Scadenza
        <Input data-testid="dc-expires" type="date" value={f.expires_at} onChange={set("expires_at")} className={inputCls} />
      </label>
      <label className="text-[10px] uppercase font-mono text-[#6b7280] space-y-1">Max utilizzi
        <Input data-testid="dc-max-uses" type="number" min="1" value={f.max_uses} onChange={set("max_uses")} placeholder="Illimitati" className={inputCls} />
      </label>
      <button data-testid="dc-submit" disabled={busy} className="h-10 aurora-gradient text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crea codice
      </button>
    </form>
  );
};

const Row = ({ c, reload }) => {
  const [label, cls] = codeState(c);
  const toggle = () => adminApi.toggleDiscountCode(c.code, !c.active).then(reload).catch(() => toast.error("Errore"));
  const del = () => window.confirm(`Eliminare il codice ${c.code}?`) && adminApi.deleteDiscountCode(c.code).then(() => { toast.success("Codice eliminato"); reload(); });
  return (
    <tr data-testid={`dc-row-${c.code}`} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
      <td className="px-4 py-3 font-mono font-medium">{c.code}
        {c.kind === "loyalty" && <span data-testid={`dc-personal-${c.code}`} className="ml-2 text-[9px] uppercase border border-[#E0262C] text-[#E0262C] px-1.5 py-0.5">Personale</span>}
      </td>
      <td className="px-4 py-3 font-display aurora-text">{c.type === "percent" ? `-${c.value}%` : `-${c.value}`}</td>
      <td className="px-4 py-3 font-mono text-xs">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}</td>
      <td className="px-4 py-3 font-mono text-xs text-[#5b6168]">{c.expires_at ? fmtDate(c.expires_at) : "Nessuna"}</td>
      <td className="px-4 py-3"><span data-testid={`dc-state-${c.code}`} className={`text-[10px] font-mono uppercase px-2 py-0.5 ${cls}`}>{label}</span></td>
      <td className="px-4 py-3 flex gap-2">
        <button data-testid={`dc-toggle-${c.code}`} onClick={toggle} className="text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-white px-2 py-1">{c.active ? "Disattiva" : "Attiva"}</button>
        <button data-testid={`dc-delete-${c.code}`} onClick={del} className="border border-[#d5dbde] hover:border-[#ef4444] hover:text-[#ef4444] px-2 py-1"><Trash2 className="w-3 h-3" /></button>
      </td>
    </tr>
  );
};

export const DiscountCodesPanel = () => {
  const [codes, setCodes] = useState(null);
  const load = useCallback(() => adminApi.discountCodes().then((d) => setCodes(d.codes)).catch(() => toast.error("Errore caricamento codici")), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-discount-codes" className="space-y-4">
      <CreateForm onCreated={load} />
      <div className="border border-[#d5dbde] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              {["Codice", "Sconto", "Utilizzi", "Scadenza", "Stato", "Azioni"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(codes || []).map((c) => <Row key={c.code} c={c} reload={load} />)}
            {codes && !codes.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessun codice sconto</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
